import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import DeleteTutorialModal from "./DeleteTutorialModal";
import { handleTextVisibility } from "../../../../../utils";
import {
  FaBookmark,
  FaHeart,
  FaRegBookmark,
  FaRegHeart,
} from "react-icons/fa6";

import DatacodeBackground from "../../../../../assets/images/datacodeTutorialBackground.png";

const TutorialListCard = ({
  id,
  title,
  tags,
  likeTutorial,
  unlikeTutorial,
  likesCount,
  userData,
  userData: { userName, email, myTutorialsLibrary },
  saveToLibrary,
  unsaveFromLibrary,
  tutorial,
  removeTutorial,
  handleEditTutorial,
  handleGetTutorialByUserName,
}) => {
  // Add this debugging right at the start
  console.log("TutorialListCard props:", {
    id,
    title,
    tags,
    tutorial,
    "tutorial.title": tutorial?.title,
    "tutorial.description": tutorial?.description,
  });

  const history = useHistory();
  const [isLiked, setLiked] = useState();
  const [isSaved, setSaved] = useState();
  const [likeCount, setLikeCount] = useState(0);
  const [openModal, setOpenModal] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    setLiked(
      tutorial
        ? tutorial.likes && tutorial.likes.find((like) => like === userName)
        : false
    );
    setLikeCount(tutorial ? tutorial.likes && tutorial.likes.length : 0);
    setSaved(
      userData &&
        userData.myTutorialsLibrary &&
        myTutorialsLibrary.find((lib) => lib === tutorial.id)
    );

    // Debug log to check if title and description are being received
    console.log("Tutorial in TutorialListCard:", tutorial);
    console.log("Title:", tutorial?.title);
    console.log("Description:", tutorial?.description);
  }, [tutorial, userData]);

  const handleLike = () => {
    if (!userName) {
      return history.push(`/login`);
    }
    likeTutorial({ id, userName }).then((res) => {
      if (res) {
        setLiked(res.data && res.data.likes.find((like) => like === userName));
        setLikeCount(res.data.likes.length);
        handleGetTutorialByUserName();
      }
    });
  };

  const handleUnLike = () => {
    unlikeTutorial({ id, userName }).then((res) => {
      if (res) {
        setLiked(res.data && res.data.likes.find((like) => like === userName));
        setLikeCount(res.data && res.data.likes.length);
        handleGetTutorialByUserName();
      }
    });
  };

  const handleSaveToLibrary = () => {
    if (!userName) {
      return history.push(`/login`);
    }
    const saveItem = tutorial.id;
    saveToLibrary({ saveItem, email }).then((res) => {
      if (res) {
        setSaved(true);
      }
    });
  };

  const handleUnsaveFromLibrary = () => {
    const saveItem = tutorial.id;
    unsaveFromLibrary({ saveItem, email }).then((res) => {
      if (res) {
        setSaved(false);
      }
    });
  };

  const toggleModal = () => {
    setOpenModal(!openModal);
  };

  const handleRemoveTutorial = () => {
    removeTutorial(id).then((res) => {
      if (res) {
        console.log(res, "res");
        toggleModal(); // Close the modal after successful removal
        // If handleGetTutorialByUserName is available, call it to refresh the list
        if (handleGetTutorialByUserName) {
          handleGetTutorialByUserName();
        }
      }
    });
  };

  return (
    <>
      <div className="row m-0 tutorial-list-card border my-2">
        <div className="col-md-4 col-4 px-0">
          <Link to={`/tutorial/${id}`}>
            <div className="position-relative h-100">
              <img
                src={tutorial?.img || DatacodeBackground}
                alt="avatar"
                loading="lazy"
                className="img-fluid h-100 w-100"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = DatacodeBackground;
                  e.target.classList.add("fallback-image");

                  const parent = e.target.parentNode;
                  if (!parent.querySelector(".fallback-title-overlay")) {
                    const overlay = document.createElement("div");
                    overlay.className = "fallback-title-overlay";
                    overlay.innerHTML = `<span>${
                      tutorial?.title || title || "No Title"
                    }</span>`;
                    parent.appendChild(overlay);
                  }
                }}
              />
            </div>
          </Link>
        </div>
        <div className="col-md-8 col-8 p-3">
          <div className="row m-0">
            <div className="col-12 p-0 d-flex justify-content-between">
              <div className="tags-input">
                <div className="tags-area border-0">
                  {tags &&
                    tags.map((tag, index) => (
                      <div className="tag align-item-center d-flex" key={index}>
                        <span className="mb-0">{tag}</span>
                      </div>
                    ))}
                </div>
              </div>
              <div>
                <span className="green font-weight-bold mb-1">
                  {likesCount}
                </span>
                {isLiked ? (
                  <i
                    onClick={() => handleUnLike()}
                    className="fas fa-heart red mx-2"
                  />
                ) : (
                  <i
                    onClick={() => handleLike()}
                    className="far fa-heart red mx-2"
                  />
                )}
                <Link to={`/tutorial/${id}`}>
                  <i className="fad fa-comments-alt voilet mx-2" />
                </Link>
                <i
                  onClick={() => handleEditTutorial(id)}
                  className="far fa-edit voilet mx-2"
                />
                <i
                  onClick={() => setOpenModal(!openModal)}
                  className="far fa-trash voilet mx-2"
                />
              </div>
            </div>
            <div>
              <Link to={`/tutorial/${id}`}>
                <h6>
                  {tutorial && tutorial.title
                    ? handleTextVisibility(tutorial.title, 50)
                    : title
                    ? handleTextVisibility(title, 50)
                    : "No Title"}
                </h6>
              </Link>
              <p>
                {tutorial && tutorial.description
                  ? handleTextVisibility(tutorial.description, 200)
                  : "No description available"}
              </p>
            </div>
          </div>
        </div>
      </div>
      <DeleteTutorialModal
        open={openModal}
        toggle={toggleModal}
        onSubmit={handleRemoveTutorial}
        submitButtonName={"Delete Tutorial"}
      />
    </>
  );
};

TutorialListCard.defaultProps = {
  userName: "",
  email: "",
  id: "",
  likesCount: 0,
  userData: {},
  tutorial: {
    title: "No Title",
    description: "No description available",
    img: "",
    id: "",
    likes: [],
  },
  myTutorialsLibrary: [],
  tags: [],
};

TutorialListCard.propTypes = {
  userName: PropTypes.string,
  id: PropTypes.string,
  likesCount: PropTypes.number,
  userData: PropTypes.object,
  tutorial: PropTypes.object,
  myTutorialsLibrary: PropTypes.array,
  tags: PropTypes.array,
};

export default TutorialListCard;
